import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Helmet } from 'react-helmet';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import './index.css';

import AuthWrapper from './components/AuthWrapper';
import AuthenticationGuard from './components/AuthenticationGuard';
import { AuthorizedApolloProvider } from './components/AuthorizedApolloProvider';
import { PrimaryButton } from './components/Button';
import Card from './components/Card';
import { HotelSessionProvider } from './hotel/session';
import RoutesIndex from './routes';
import { initializeSentry } from './sentry';
import { store } from './store/store';
import { PortalTermsGate } from './terms/PortalTermsGate';

// Initialize Sentry before React app
initializeSentry();

// Custom error fallback UI component
function ErrorFallback({
  error,
  resetError,
}: {
  error: unknown;
  resetError: () => void;
}) {
  const [showDetails, setShowDetails] = React.useState(false);

  // Convert error to Error object for display
  const errorObj =
    error instanceof Error
      ? error
      : new Error(String(error || 'Unknown error'));

  return (
    <div className="flex min-h-screen items-center justify-center bg-backgroundLightGray p-4">
      <Card className="max-w-2xl">
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="shrink-0">
              <svg
                className="size-6 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">
                Something went wrong
              </h1>
              <p className="mt-2 text-gray-600">
                We apologize for the inconvenience. An error has occurred and
                our team has been notified.
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-primaryBlue hover:text-primaryDarkBlue"
            >
              {showDetails ? 'Hide' : 'Show'} error details
            </button>

            {showDetails && (
              <div className="mt-3 rounded-md bg-gray-50 p-4">
                <p className="mb-2 text-sm font-semibold text-gray-700">
                  Error Message:
                </p>
                <p className="mb-3 text-sm text-gray-600">
                  {errorObj.message || 'Unknown error'}
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4">
            <PrimaryButton onClick={resetError}>Try Again</PrimaryButton>
          </div>
        </div>
      </Card>
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog={false}>
        <Provider store={store}>
          <AuthWrapper>
            <AuthenticationGuard>
              <HotelSessionProvider>
                <AuthorizedApolloProvider>
                  <Helmet
                    defaultTitle="Client Portal | Team Housing Solutions"
                    titleTemplate="%s | Client Portal | Team Housing Solutions"
                  ></Helmet>
                  <PortalTermsGate>
                    <RoutesIndex />
                  </PortalTermsGate>
                </AuthorizedApolloProvider>
              </HotelSessionProvider>
            </AuthenticationGuard>
          </AuthWrapper>
        </Provider>
      </Sentry.ErrorBoundary>
    ),
  },
]);

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
);
